import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { mapCustomerBookingDetail, type CustomerBookingDetailVM } from '@/lib/booking/customerBookingDetail';

export function useCustomerBookingDetail(bookingId: string | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['customer-booking-detail', bookingId],
    queryFn: async (): Promise<CustomerBookingDetailVM> => {
      if (!bookingId || !user?.email) throw new Error('Missing booking ID or user');

      const { data, error } = await supabase
        .from('accommodation_bookings')
        .select(`
          id, accommodation_id, producer_id,
          check_in, check_out, guests, guest_name,
          guest_email, guest_phone, guest_message,
          status, booking_code, created_at, cancelled_at,
          payment_status, requires_prepayment, prepayment_amount_ore,
          amount_paid_ore, paid_at,
          producer_accommodations!accommodation_bookings_accommodation_id_fkey ( title ),
          producers!accommodation_bookings_producer_id_fkey ( name )
        `)
        .eq('id', bookingId)
        .eq('guest_email_norm', user.email.toLowerCase())
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('Bokningen hittades inte');

      const row = data as any;

      // Aggregate payments from canonical source (payment records)
      const { data: paymentRows } = await supabase
        .from('accommodation_booking_payments')
        .select('amount_ore')
        .eq('booking_id', bookingId);

      const payments = paymentRows ?? [];
      const aggregatedPaidOre = payments.reduce((sum, p) => sum + p.amount_ore, 0);
      const paymentCount = payments.length;

      // totalStayOre not available at this level — pass null for graceful fallback
      const totalStayOre: number | null = null;

      return mapCustomerBookingDetail({
        id: row.id,
        accommodation_title: row.producer_accommodations?.title ?? 'Boende',
        producer_name: row.producers?.name ?? 'Okänd värd',
        check_in: row.check_in,
        check_out: row.check_out,
        guests: row.guests,
        guest_name: row.guest_name,
        guest_email: row.guest_email,
        guest_phone: row.guest_phone,
        guest_message: row.guest_message,
        status: row.status,
        booking_code: row.booking_code,
        created_at: row.created_at,
        cancelled_at: row.cancelled_at,
        payment_status: row.payment_status ?? 'none',
        requires_prepayment: row.requires_prepayment,
        prepayment_amount_ore: row.prepayment_amount_ore,
        amount_paid_ore: aggregatedPaidOre,
        paid_at: row.paid_at,
        total_stay_ore: totalStayOre,
        payment_count: paymentCount,
      });
    },
    enabled: !!bookingId && !!user,
  });
}
